import type { DbMeetingAttendee } from "@/types/db";

function attendeeNameKey(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Merge attendees from a linked previous meeting into the current attendee
 * list without introducing duplicates. An incoming attendee is skipped when
 * the current list already contains an entry with the same non-null
 * `user_id`, or with the same case-insensitive name. Duplicates within the
 * incoming list itself are also collapsed. Returns the same array reference
 * when nothing new is added so callers can cheaply detect a no-op.
 */
export function mergeMeetingAttendees(
  current: DbMeetingAttendee[],
  incoming: DbMeetingAttendee[]
): DbMeetingAttendee[] {
  const seenUserIds = new Set(
    current
      .map((attendee) => attendee.user_id)
      .filter((value): value is string => Boolean(value))
  );
  const seenNames = new Set(current.map((attendee) => attendeeNameKey(attendee.name)));

  const additions: DbMeetingAttendee[] = [];

  for (const attendee of incoming) {
    const nameKey = attendeeNameKey(attendee.name);
    if (!nameKey) {
      continue;
    }

    if (attendee.user_id && seenUserIds.has(attendee.user_id)) {
      continue;
    }

    if (seenNames.has(nameKey)) {
      continue;
    }

    additions.push({ name: attendee.name.trim(), user_id: attendee.user_id });
    if (attendee.user_id) {
      seenUserIds.add(attendee.user_id);
    }
    seenNames.add(nameKey);
  }

  return additions.length > 0 ? [...current, ...additions] : current;
}
